import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { ApiError } from '@/api/http';
import { Panel, PanelHeader, Skeleton, ReadFailed } from '@/components/ui';
import { DecisionDetail } from '@/components/DecisionDetail';
import { cn } from '@/lib/cn';
import { FAMILIES_REQUIRED } from '@/components/PromoteDetection';
import { say } from '@/lib/vocabulary';

/**
 * The six chart series the backend has always served and nothing ever asked
 * for.
 *
 * Each answers AVAILABLE with a payload or UNAVAILABLE with a reason, and both
 * are real answers: a series that cannot be computed says why, in the same
 * place it would have drawn, rather than being hidden so the page looks fuller
 * than the data supports.
 *
 * Every number drawn is the API's own. Nothing is interpolated, no axis is
 * padded to look better, and a truncated series says it was truncated.
 */

const SERIES = [
  { slug: 'attribution-funnel', title: 'How far each file got', sub: 'listed, probed, parsed, reconstructed, validated' },
  { slug: 'ledger-disposition', title: 'Does the ledger balance', sub: 'assigned plus unresolved plus contradicted against the manifest' },
  { slug: 'evidence-gate', title: 'Distance to proven', sub: 'signals and evidence families behind each detection' },
  { slug: 'entity-class-distribution', title: 'What kinds of thing are in here', sub: 'by entity class' },
  { slug: 'activity', title: 'When this data was made', sub: 'over time, by entity class' },
  { slug: 'timestamp-span', title: 'How current it is', sub: 'earliest and latest, native and observed' },
] as const;

/** Stage names as an operator would read them. */
const STAGE: Record<string, string> = {
  MANIFEST_ITEMS: 'listed',
  PROBED: 'opened',
  PARSED: 'read',
  RECONSTRUCTED: 'turned into records',
  VALIDATED: 'checked',
};

type Chart = Record<string, unknown>;

function num(v: unknown): number {
  return typeof v === 'number' ? v : 0;
}

function Row({
  label,
  value,
  share,
  detail,
  tone = 'tool',
}: {
  label: string;
  value: string;
  share: number;
  detail?: string;
  tone?: 'tool' | 'stray';
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2">
      <span className="w-40 flex-none truncate text-small text-ink" title={label}>
        {label}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block h-1.5 w-full overflow-hidden rounded-full bg-panel-2">
          <span
            className={cn('block h-full rounded-full', tone === 'tool' ? 'bg-acc' : 'bg-line-2')}
            style={{ width: `${Math.max(share * 100, share > 0 ? 1.5 : 0)}%` }}
          />
        </span>
      </span>
      <span className="mono w-28 flex-none text-right text-caption text-ink-2">{value}</span>
      {detail !== undefined && (
        <span className="mono hidden w-32 flex-none text-right text-caption text-ink-4 sm:block">{detail}</span>
      )}
    </div>
  );
}

function Funnel({ chart }: { chart: Chart }) {
  const stages = (chart.stages as { stage: string; value: number; basis?: string }[]) ?? [];
  const top = Math.max(...stages.map((s) => num(s.value)), 1);
  return (
    <>
      {stages.map((s) => (
        <Row
          key={s.stage}
          label={STAGE[s.stage] ?? say(s.stage)}
          value={`${num(s.value).toLocaleString()} files`}
          share={num(s.value) / top}
          tone={num(s.value) === 0 ? 'stray' : 'tool'}
        />
      ))}
    </>
  );
}

function Ledger({ chart }: { chart: Chart }) {
  const assigned = num(chart.assigned);
  const unresolved = num(chart.unresolved);
  const contradicted = num(chart.contradicted);
  const total = num(chart.manifestTotal) || assigned + unresolved + contradicted || 1;
  // Absent and false are different answers, so they get different sentences.
  const balances = typeof chart.equationValid === 'boolean' ? chart.equationValid : null;
  return (
    <>
      <Row label="traced to a tool" value={`${assigned.toLocaleString()} files`} share={assigned / total} />
      <Row label="not traced" value={`${unresolved.toLocaleString()} files`} share={unresolved / total} tone="stray" />
      <Row
        label="rules disagreed"
        value={`${contradicted.toLocaleString()} files`}
        share={contradicted / total}
        tone="stray"
      />
      <p className={cn('border-t border-line px-4 py-2.5 text-caption', balances ? 'text-ink-3' : 'text-warn')}>
        {balances
          ? `Adds up: ${assigned.toLocaleString()} + ${unresolved.toLocaleString()} + ${contradicted.toLocaleString()} equals the ${total.toLocaleString()} files listed. Nothing is missing and nothing is counted twice.`
          : balances === false
              ? 'These do not add up to the manifest, which means this bucket cannot be reported as complete.'
              : 'This chart carried no completeness check, so whether these add up is unknown.'}
      </p>
    </>
  );
}

function EvidenceGate({ chart }: { chart: Chart }) {
  const decisions = (chart.decisions as { decisionId: string; signals: number; families: number; contradictions: number }[]) ?? [];
  const top = Math.max(...decisions.map((d) => num(d.signals)), 1);
  // Each row names a decision, and the decision route explains it down to the
  // evidence. Without this the gate verdict is something to take on trust.
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      {decisions.slice(0, 12).map((d) => (
        <div key={d.decisionId}>
          <button
            onClick={() => setOpen(open === d.decisionId ? null : d.decisionId)}
            className="block w-full text-left transition-colors duration-fast hover:bg-hov"
            aria-expanded={open === d.decisionId}
          >
            <Row
              label={`${num(d.families)} of ${FAMILIES_REQUIRED} kinds of check`}
              value={`${num(d.signals).toLocaleString()} signals`}
              share={num(d.signals) / top}
              detail={num(d.contradictions) > 0 ? `${num(d.contradictions)} disagreed` : 'why?'}
              tone={num(d.families) >= 5 ? 'tool' : 'stray'}
            />
          </button>
          {open === d.decisionId && (
            <div className="border-y border-line bg-panel-2">
              <DecisionDetail decisionId={d.decisionId} />
            </div>
          )}
        </div>
      ))}
      <p className="border-t border-line px-4 py-2.5 text-caption text-ink-3">
        A tool counts as proven at ten files across five different kinds of check. Open a row to see the evidence it
        rests on.
        {chart.truncated === true ? ` Showing the first ${Math.min(decisions.length, 12)} of more.` : ''}
      </p>
    </>
  );
}

function Distribution({ chart }: { chart: Chart }) {
  const classes = (chart.classes as { entityClass: string; value: number }[]) ?? [];
  const top = Math.max(...classes.map((c) => num(c.value)), 1);
  return (
    <>
      {classes.slice(0, 15).map((c) => (
        <Row
          key={c.entityClass}
          label={say(c.entityClass)}
          value={num(c.value).toLocaleString()}
          share={num(c.value) / top}
        />
      ))}
      {chart.truncated === true && (
        <p className="border-t border-line px-4 py-2.5 text-caption text-ink-3">
          Truncated by the backend at {num(chart.maxClasses)} classes.
        </p>
      )}
    </>
  );
}

function Activity({ chart }: { chart: Chart }) {
  const points = (chart.points as { t: string; entityClass: string; value: number }[]) ?? [];
  // Sum across classes per bucket of time, because a stacked line of unknown
  // arity is unreadable and the shape is what matters here.
  const byTime = new Map<string, number>();
  for (const p of points) byTime.set(p.t, (byTime.get(p.t) ?? 0) + num(p.value));
  const series = [...byTime.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  const top = Math.max(...series.map(([, v]) => v), 1);
  if (series.length === 0) return null;
  return (
    <div className="px-4 py-3">
      <div className="flex h-24 items-end gap-px">
        {series.map(([t, v]) => (
          <span
            key={t}
            title={`${new Date(t).toLocaleDateString()} · ${v.toLocaleString()}`}
            className="min-w-0 flex-1 rounded-t-sm bg-acc"
            style={{ height: `${Math.max((v / top) * 100, 2)}%` }}
          />
        ))}
      </div>
      <div className="mono mt-1.5 flex justify-between text-caption text-ink-4">
        <span>{new Date(series[0]![0]).toLocaleDateString()}</span>
        <span>
          {chart.interval ? String(chart.interval).toLowerCase() : ''} · peak {top.toLocaleString()}
        </span>
        <span>{new Date(series[series.length - 1]![0]).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function Span({ chart }: { chart: Chart }) {
  const when = (v: unknown) =>
    typeof v === 'string' && v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium' }) : '—';
  const rows: [string, unknown, unknown][] = [
    ['earliest in the data', chart.earliestNative, chart.nativeBasis],
    ['latest in the data', chart.latestNative, chart.nativeBasis],
    ['export produced', chart.exportGeneratedAt, chart.exportGeneratedAtBasis],
    ['last touched in S3', chart.s3LatestModified, chart.s3LatestModifiedBasis],
  ];
  return (
    <>
      {rows.map(([label, value, basis]) => (
        <div key={label} className="flex flex-wrap items-baseline gap-x-3 border-b border-line px-4 py-2 last:border-0">
          <span className="w-44 flex-none text-small text-ink">{label}</span>
          <span className="mono text-small text-ink-2">{when(value)}</span>
          {typeof basis === 'string' && basis && (
            <span className="text-caption text-ink-4">{say(basis)}</span>
          )}
        </div>
      ))}
    </>
  );
}

const PAYLOAD_KEY: Record<string, string> = {
  'attribution-funnel': 'attributionFunnel',
  'ledger-disposition': 'ledgerDisposition',
  'evidence-gate': 'evidenceGate',
  'entity-class-distribution': 'entityClassDistribution',
  activity: 'activity',
  'timestamp-span': 'timestampSpan',
};

function Body({ slug, data }: { slug: string; data: Record<string, unknown> }) {
  const chart = data[PAYLOAD_KEY[slug]!] as Chart | undefined;
  if (!chart) return null;
  switch (slug) {
    case 'attribution-funnel':
      return <Funnel chart={chart} />;
    case 'ledger-disposition':
      return <Ledger chart={chart} />;
    case 'evidence-gate':
      return <EvidenceGate chart={chart} />;
    case 'entity-class-distribution':
      return <Distribution chart={chart} />;
    case 'activity':
      return <Activity chart={chart} />;
    case 'timestamp-span':
      return <Span chart={chart} />;
    default:
      return null;
  }
}

export function BucketCharts({ bucketId }: { bucketId: string }) {
  const charts = useQueries({
    queries: SERIES.map((s) => ({
      queryKey: ['bucket-chart', bucketId, s.slug],
      queryFn: ({ signal }: { signal?: AbortSignal }) => backend.getBucketChart(bucketId, s.slug, signal),
      retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
      staleTime: 60_000,
    })),
  });

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {SERIES.map((s, i) => {
        const q = charts[i]!;
        const data = q.data as unknown as Record<string, unknown> | undefined;
        const available = data?.availability === 'AVAILABLE';
        return (
          <Panel key={s.slug}>
            <PanelHeader title={s.title} sub={s.sub} />
            {q.isPending ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 3 }).map((_, k) => (
                  <Skeleton key={k} className="h-5 w-full" />
                ))}
              </div>
            ) : q.isError ? (
              <ReadFailed
                what={q.error instanceof ApiError ? (q.error.reason ?? q.error.message) : 'The API did not respond.'}
                onRetry={() => void q.refetch()}
              />
            ) : available ? (
              <Body slug={s.slug} data={data!} />
            ) : (
              <p className="px-4 py-3 text-small text-ink-3">
                {typeof data?.reason === 'string' && data.reason
                  ? data.reason
                  : 'The backend reports this cannot be computed for this bucket yet.'}
              </p>
            )}
          </Panel>
        );
      })}
    </div>
  );
}
